import type { UserModel } from './types'
import * as type from './types'

// Types
import type { Action } from '../../util/redux'
import type { AuthorizeUserResponse } from '@rachio-npm/com-rachio-web-core/types/user/userService'

// Type defs //////////////////////////////////////////////////////////////////

export type UserReducerMETA = {
  +currentUser?: ?AuthorizeUserResponse,
  +currentUserId: ?string,
  +loginStatus: string
}

export type Store = {
  +META: UserReducerMETA,
  +[user_id: string]: UserModel
}

// Reducer ////////////////////////////////////////////////////////////////////

export const initialState = {
  META: {
    currentUser: null
  }
}

const user = (state: Store = initialState, action: Action): Store => {
  switch (action.type) {
    case type.USER_CREATE_INIT:
      return {
        ...state,
        META: { ...state.META, createStatus: 'CREATING' }
      }
    case type.USER_CREATE_COMPLETE:
      if (action.error) {
        return {
          ...state,
          META: { ...state.META, createStatus: action }
        }
      }
      if (action.payload && !action.payload.user) {
        return {
          ...state,
          META: { ...state.META, createStatus: 'CREATE_RESPONSE_RECEIVED' }
        }
      } else {
        return {
          ...state,
          META: {
            ...state.META,
            createStatus: action
          },
          [action.payload.user.id]: action.payload.user
        }
      }
    case type.LOGIN_INIT:
      return {
        ...state,
        META: { ...state.META, currentUserId: null, loginStatus: 'LOGGINGIN' }
      }
    case type.LOGIN_COMPLETE:
      if (action.error) {
        return {
          ...state,
          META: {
            ...state.META,
            currentUserId: null,
            loginStatus: action.payload
          }
        }
      } else {
        return {
          ...state,
          META: {
            ...state.META,
            currentUser: action.payload
          },
          [action.payload.user_id]: {
            ...(state[action.payload.user_id] || {}),
            ...action.payload,
            META: {}
          }
        }
      }
    case type.LOGOUT:
      return {
        ...state,
        META: {
          ...state.META,
          currentUser: {}
        }
      }
    case type.USER_REQUEST_INIT:
      return {
        ...state,
        [action.payload]: {
          ...state[action.payload],
          META: {
            ...((state[action.payload] || {}).META || {}),
            requestStatus: 'REQUESTING'
          }
        }
      }
    case type.USER_REQUEST_COMPLETE:
      if (action.error) {
        return state
      } else {
        return {
          ...state,
          [action.payload.user.id]: {
            ...state[action.payload.user.id],
            ...action.payload.user,
            META: {
              ...state[action.payload.user.id].META,
              requestStatus: 'REQUEST_SUCCESSFUL',
              lastPulled: action.payload.lastPulled
            }
          }
        }
      }
    case type.UPDATE:
      return {
        ...state,
        [action.payload.id]: {
          META: (state[action.payload.id] || {}).META || {},
          ...action.payload
        }
      }
    case type.USER_INVALIDATE_DATA:
      return {
        ...state,
        [action.payload.userId]: {
          ...state[action.payload.userId],
          META: {
            ...((state[action.payload.userId] || {}).META || {}),
            valid: false
          }
        }
      }
    case type.GET_LOCATIONS_INIT:
      return {
        ...state,
        [action.payload.userId]: {
          ...state[action.payload.userId],
          META: {
            ...(state[action.payload.userId] || {}).META,
            fetchingLocations: true
          }
        }
      }
    case type.GET_LOCATIONS_COMPLETE:
      if (action.error) {
        return {
          ...state,
          [action.payload.userId]: {
            ...state[action.payload.userId],
            META: {
              ...state[action.payload.userId].META,
              fetchingLocations: action
            }
          }
        }
      } else {
        return {
          ...state,
          [action.payload.userId]: {
            ...state[action.payload.userId],
            META: {
              ...state[action.payload.userId].META,
              locationIds: action.payload.locationIds,
              fetchingLocations: false
            }
          }
        }
      }
    case type.ADD_LOCATION:
      return {
        ...state,
        [action.payload.userId]: {
          ...state[action.payload.userId],
          META: {
            ...state[action.payload.userId].META,
            locationIds: (
              state[action.payload.userId].META.locationIds || []
            ).includes(action.payload.locationId)
              ? state[action.payload.userId].META.locationIds || []
              : (state[action.payload.userId].META.locationIds || []).concat([
                  action.payload.locationId
                ])
          }
        }
      }
    case type.SET_PREFERENCE:
      return {
        ...state,
        [action.payload.userId]: {
          ...state[action.payload.userId],
          preference: [action.payload.preference]
            .concat(
              ((state[action.payload.userId] || {}).preference || []).map(
                pref =>
                  pref.name === action.payload.preference.name ? null : pref
              )
            )
            .filter(pref => pref)
        }
      }
    case type.SET_DEVICES_IDS:
      return {
        ...state,
        [action.payload.userId]: {
          ...state[action.payload.userId],
          META: {
            ...(state[action.payload.userId] || {}).META,
            deviceIds: action.payload.deviceIds
          }
        }
      }
    case type.REMOVE_LOCATION:
      return {
        ...state,
        [action.payload.userId]: {
          ...state[action.payload.userId],
          META: {
            ...state[action.payload.userId].META,
            locationIds: (
              state[action.payload.userId].META.locationIds || []
            ).filter(locId => locId !== action.payload.locationId)
          }
        }
      }
    default:
      return state
  }
}

export default user
